import { useMemo } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { mdiAlertOutline } from '@mdi/js'
import { $api } from '../../api/queryClient'

type Props = {
    org: string
    docUid: string
    /** The document's filing asset — data_consistency issues target it. */
    assetUid: string | null | undefined
}

/**
 * Discreet warning shown on the document page when an open data_consistency
 * issue (check=document_address_mismatch, created by the backend's
 * doc_address_consistency job — spec 2026-07-17-02) references this
 * document. The issue targets the ASSET (that's where it is triaged), so it
 * is found via the asset's open issues and matched on
 * properties.document_uid. Clicking opens the issue sheet in place.
 */
export function DocumentAddressMismatchBanner({ org, docUid, assetUid }: Props) {
    const { t } = useTranslation('issues')
    const navigate = useNavigate()

    const { data } = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/issues',
        {
            params: {
                path: { orgCode: org },
                query: {
                    asset_uid: assetUid ?? '',
                    type: 'data_consistency',
                    status: 'open,in_progress',
                    assignee_uid: 'all',
                    limit: 50,
                },
            },
        },
        { enabled: Boolean(assetUid) },
    )

    const mismatchIssue = useMemo(() => {
        const items = (data?.data ?? []) as Array<{
            uid: string
            properties?: { check?: string; document_uid?: string }
        }>
        return items.find(
            (issue) =>
                issue.properties?.check === 'document_address_mismatch' &&
                issue.properties?.document_uid === docUid,
        )
    }, [data, docUid])

    if (!mismatchIssue) return null

    return (
        <button
            type="button"
            onClick={() =>
                navigate({
                    search: (prev: Record<string, unknown>) =>
                        ({ ...prev, issue: mismatchIssue.uid }) as never,
                })
            }
            title={t('data_consistency.card_title')}
            data-testid="document-address-mismatch-banner"
            className="w-full flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive hover:bg-destructive/10 transition-colors text-left"
        >
            <AppIcon path={mdiAlertOutline} size="sm" className="shrink-0" />
            <span className="min-w-0 truncate">{t('data_consistency.document_badge')}</span>
        </button>
    )
}
